import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLang, translations } from '../i18n';
import LanguageDropdownCompact from '../components/LanguageDropdownCompact';
import './Login.css';

const API_FORGOT_PASSWORD_3 = process.env.REACT_APP_API_USERS + "/forgot-password-3" || '';

const ForgotPassword3: React.FC = () => {
  const { lang } = useLang();
  const t = translations[lang];
  const navigate = useNavigate();
  const location = useLocation();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const token = location.state?.token;
  const email = location.state?.email;

  // Rediriger vers l'étape 1 si pas de token
  useEffect(() => {
    if (!token || !email) {
      navigate('/forgot-password-1');
    }
  }, [token, email, navigate]);

  // Vérifier en temps réel les règles du mot de passe
  const passwordRulesCheck = {
    length: newPassword.length >= 12,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /\d/.test(newPassword),
    symbol: /[_!@#$%^&*(),.?":{}|<>]/.test(newPassword),
    noEmail: email ? !newPassword.toLowerCase().includes(email.split('@')[0].toLowerCase()) : true
  };

  const isPasswordValid = Object.values(passwordRulesCheck).every(Boolean);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword !== '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Vérifier que les mots de passe correspondent
    if (!passwordsMatch) {
      setError(t.passwordMismatch);
      return;
    }

    // Vérifier que le mot de passe respecte les règles
    if (!isPasswordValid) {
      setError(t.passwordInvalid);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(API_FORGOT_PASSWORD_3, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          email: email,
          new_password: newPassword 
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la réinitialisation');
      }

      // Si succès, afficher le message et rediriger vers login
      setSuccess(t.forgotPasswordNewSuccess);
      
      // Rediriger après 2 secondes
      setTimeout(() => {
        navigate('/login');
      }, 2000);

    } catch (err) {
      setError(t.forgotPasswordNewError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <form className="login-form" onSubmit={handleSubmit}>
        <div style={{display:'flex',justifyContent:'center',marginBottom:24,position:'relative'}}>
          <img src={process.env.PUBLIC_URL + '/recat-logo-1024.png'} alt="Logo" style={{height:80}} />
          <div style={{position:'absolute',right:0,top:0}}>
            <LanguageDropdownCompact />
          </div>
        </div>
        
        <h2>{t.forgotPasswordNewTitle}</h2>
        <p style={{ margin: '0 0 15px 0', color: '#666', textAlign: 'center', fontSize: '0.95rem' }}>
          {t.forgotPasswordNewDescription} <strong>{email}</strong>
        </p>

        <input
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder={t.forgotPasswordNewLabel}
          required
          disabled={loading}
        />

        <input
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder={t.forgotPasswordNewConfirmLabel}
          required
          disabled={loading}
        />

        {/* Indicateurs de validation du mot de passe */}
        {newPassword && (
          <div style={{ marginBottom: '10px', fontSize: '12px', lineHeight: '1.6' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '5px', color: '#555' }}>{t.passwordRules}</div>
            <div style={{ color: passwordRulesCheck.length ? '#2aa04aff' : '#d32f2f' }}>
              {passwordRulesCheck.length ? '✓' : '✗'} {t.passwordRuleLength}
            </div>
            <div style={{ color: passwordRulesCheck.uppercase ? '#2aa04aff' : '#d32f2f' }}>
              {passwordRulesCheck.uppercase ? '✓' : '✗'} {t.passwordRuleUppercase}
            </div>
            <div style={{ color: passwordRulesCheck.lowercase ? '#2aa04aff' : '#d32f2f' }}>
              {passwordRulesCheck.lowercase ? '✓' : '✗'} {t.passwordRuleLowercase}
            </div>
            <div style={{ color: passwordRulesCheck.number ? '#2aa04aff' : '#d32f2f' }}>
              {passwordRulesCheck.number ? '✓' : '✗'} {t.passwordRuleNumber}
            </div>
            <div style={{ color: passwordRulesCheck.symbol ? '#2aa04aff' : '#d32f2f' }}>
              {passwordRulesCheck.symbol ? '✓' : '✗'} {t.passwordRuleSymbol}
            </div>
            <div style={{ color: passwordRulesCheck.noEmail ? '#2aa04aff' : '#d32f2f' }}>
              {passwordRulesCheck.noEmail ? '✓' : '✗'} Ne pas contenir votre email
            </div>
          </div>
        )}

        {/* Indicateur de correspondance des mots de passe */}
        {confirmPassword && (
          <div style={{ marginBottom: '10px', fontSize: '12px' }}>
            <div style={{ color: passwordsMatch ? '#2aa04aff' : '#d32f2f' }}>
              {passwordsMatch ? '✓' : '✗'} {t.passwordMismatch}
            </div>
          </div>
        )}

        {error && <div className="login-error">{error}</div>}
        {success && <div style={{ color: '#2aa04aff', background: '#e8f5e9', borderRadius: '6px', padding: '8px 10px', textAlign: 'center', fontSize: '0.98rem' }}>{success}</div>}

        <button type="submit" disabled={loading || !isPasswordValid || !passwordsMatch}>
          {loading ? t.forgotPasswordNewLoading : t.forgotPasswordNewBtn}
        </button>
      </form>
    </div>
  );
};

export default ForgotPassword3;
